import type { FC, ReactNode } from 'react'

import {
    Facebook,
    Instagram,
    Linkedin,
    Mail,
    MapPin,
    MessageCircle,
    Phone
} from 'lucide-react'
import Container from '@/components/landing/container'
import Logo from '@/components/landing/logo'

import {
    CONTACT,
    COURSES,
    LINKS,
    NAV_ITEMS
} from '@/constants/landing'

const SOCIAL_LINKS = [
    { label: "Skillkafe on Facebook", href: LINKS.facebook, icon: Facebook },
    { label: "Skillkafe on Instagram", href: LINKS.instagram, icon: Instagram },
    { label: "Skillkafe on LinkedIn", href: LINKS.linkedin, icon: Linkedin }
]

const LEARN_LINKS = [
    ...COURSES.map(course => ({ label: course.name, href: course.href })),
    { label: "SkillKafe Degrees", href: LINKS.eduKafe },
    { label: "Learning portal", href: LINKS.learningPortal },
    { label: "Blog", href: LINKS.blog }
]

const linkClass = 'text-[15px] text-white/70 transition-colors hover:text-white'

const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='bg-brand-navy text-white'>
            <Container className='py-16 sm:py-20'>
                <div className='grid gap-12 lg:grid-cols-12'>
                    <div className='lg:col-span-4'>
                        <Logo variant='white' />

                        <p className='mt-6 max-w-sm leading-relaxed text-white/70'>
                            Empowering careers with industry-ready skills for a brighter future.
                        </p>

                        <ul className='mt-7 flex gap-3'>
                            {SOCIAL_LINKS.map(social => {
                                const Icon = social.icon

                                return (
                                    <li key={social.href}>
                                        <a
                                            href={social.href}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            aria-label={social.label}
                                            className='grid size-11 place-items-center rounded-full bg-white/10 text-white ring-1 ring-white/15 transition-colors hover:bg-white/20'
                                        >
                                            <Icon className='size-5' aria-hidden='true' />
                                        </a>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>

                    <div className='grid gap-10 sm:grid-cols-3 lg:col-span-8'>
                        <nav aria-labelledby='footer-explore'>
                            <h2 id='footer-explore' className='text-sm font-semibold uppercase tracking-[0.16em] text-brand-teal-bright'>
                                Explore
                            </h2>

                            <ul className='mt-5 space-y-3'>
                                {NAV_ITEMS.map(item => (
                                    <li key={item.href}>
                                        <a href={item.href} className={linkClass}>
                                            {item.label}
                                        </a>
                                    </li>
                                ))}

                                <li>
                                    <a href='#team' className={linkClass}>
                                        Our story
                                    </a>
                                </li>
                            </ul>
                        </nav>

                        <nav aria-labelledby='footer-learn'>
                            <h2 id='footer-learn' className='text-sm font-semibold uppercase tracking-[0.16em] text-brand-teal-bright'>
                                Learn
                            </h2>

                            <ul className='mt-5 space-y-3'>
                                {LEARN_LINKS.map(link => (
                                    <li key={link.href}>
                                        <a href={link.href} target='_blank' rel='noopener noreferrer' className={linkClass}>
                                            {link.label}
                                            <span className='sr-only'> (opens in a new tab)</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <div>
                            <h2 className='text-sm font-semibold uppercase tracking-[0.16em] text-brand-teal-bright'>
                                Contact
                            </h2>

                            <ul className='mt-5 space-y-4'>
                                <li>
                                    <a href={CONTACT.phoneHref} className={`${linkClass} inline-flex items-start gap-3`}>
                                        <Phone className='mt-0.5 size-4 shrink-0 text-brand-teal-bright' aria-hidden='true' />
                                        {CONTACT.phoneDisplay}
                                    </a>
                                </li>

                                <li>
                                    <a href={LINKS.whatsappLearner} target='_blank' rel='noopener noreferrer' className={`${linkClass} inline-flex items-start gap-3`}>
                                        <MessageCircle className='mt-0.5 size-4 shrink-0 text-brand-teal-bright' aria-hidden='true' />
                                        <span>
                                            WhatsApp {CONTACT.whatsappDisplay}
                                            <span className='sr-only'> (opens in a new tab)</span>
                                        </span>
                                    </a>
                                </li>

                                <li>
                                    <a href={`mailto:${CONTACT.email}`} className={`${linkClass} inline-flex items-start gap-3`}>
                                        <Mail className='mt-0.5 size-4 shrink-0 text-brand-teal-bright' aria-hidden='true' />
                                        {CONTACT.email}
                                    </a>
                                </li>

                                <li>
                                    <a href={CONTACT.mapsHref} target='_blank' rel='noopener noreferrer' className={`${linkClass} inline-flex items-start gap-3`}>
                                        <MapPin className='mt-0.5 size-4 shrink-0 text-brand-teal-bright' aria-hidden='true' />
                                        <span>
                                            {CONTACT.addressLines.map(line => (
                                                <span key={line} className='block'>
                                                    {line}
                                                </span>
                                            ))}
                                            <span className='sr-only'> (opens Google Maps in a new tab)</span>
                                        </span>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className='mt-14 flex flex-col gap-4 border-t border-white/10 pt-8 text-sm text-white/60 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        &copy; 2026 Sai Garuda Education. All rights reserved.
                    </p>

                    <ul className='flex flex-wrap gap-x-6 gap-y-2'>
                        <li>
                            <a href={LINKS.privacy} target='_blank' rel='noopener noreferrer' className='transition-colors hover:text-white'>
                                Privacy Policy
                            </a>
                        </li>

                        <li>
                            <a href={LINKS.terms} target='_blank' rel='noopener noreferrer' className='transition-colors hover:text-white'>
                                Terms and Conditions
                            </a>
                        </li>
                    </ul>
                </div>
            </Container>
        </footer>
    )
}

export default SiteFooter
